// src/pages/SelectionOrders/subpages/SelectionOrderEdit.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import { Tab, Tabs } from 'react-bootstrap';

import useSelectionOrderService from '../../../app/services/hooks/useSelectionOrderService';
import { AppContext } from '../../../contexts/AppContext';
import OrderHistoryTab from '../components/tables/OrderHistoryTab';

import {
  SelectionOrder,
  SelectionOrderUpdatePayload,
  SelectionOrderBatchRow,
  SelectionOrderCollectorRow,
  SelectionOrderDestinationBatchRow,
} from '../../../app/models/SelectionOrder';

import { formatDateToInputDate } from '../../../helpers';
import SelectionOrderHeaderForm from '../components/forms/SelectionOrderHeaderForm';
import OrderBatchesTab from '../components/tables/OrderBatchesTab';
import OrderCollectorsTab from '../components/tables/OrderCollectorsTab';
import OrderIncidentsTab from '../components/tables/OrderIncidentsTab';

type RouterParams = { id: string };
type SelectOpt = { id: number | string; name: string };

const SelectionOrdersEdit: React.FC = () => {
  const { id } = useParams<RouterParams>();
  const orderId = Number(id);

  const { showLoading, hideLoading } = useContext(AppContext);
  const {
    editSelectionOrder,
    updateSelectionOrder,
    fetchingEditSelectionOrder,
    fetchingUpdateSelectionOrder,
  } = useSelectionOrderService();

  // REFS para evitar bucles
  const editRef = useRef(editSelectionOrder);
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  useEffect(() => { editRef.current = editSelectionOrder; }, [editSelectionOrder]);
  useEffect(() => { showLoadingRef.current = showLoading; }, [showLoading]);
  useEffect(() => { hideLoadingRef.current = hideLoading; }, [hideLoading]);
  const [reloadTick, setReloadTick] = useState(0);

  const [verifying, setVerifying] = useState<boolean>(true);

  const [order, setOrder] = useState<SelectionOrder>({
    status: 'CREATED',
    is_active: true,
    collectors: [],
    pending_batches: [],
    loaded_batches: [],
    destination_batches: [],
  });

  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [statuses, setStatuses] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  const load = useCallback((oid: number) => {
    showLoadingRef.current?.('loading', 'Cargando orden...');
    editRef.current(oid, {
      onSuccess: (resp: ServiceResponse) => {
        const o = resp.data.selection_order as SelectionOrder;
        setOrder({
          ...o,
          collectors: o.collectors ?? [],
          pending_batches: o.pending_batches ?? [],
          loaded_batches: o.loaded_batches ?? [],
          destination_batches: o.destination_batches ?? [],
        });
        setSocieties(resp.data.societies || []);
        setUnits(resp.data.units || []);
        setStatuses(resp.data.status || []);
        hideLoadingRef.current?.();
        setVerifying(false); 
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message, { autoClose: 2500 });
        setVerifying(false);
      }
    });
  }, []);

  useEffect(() => { if (Number.isFinite(orderId)) load(orderId); }, [orderId, load]);

  const onSaveHeader = (payload: SelectionOrderUpdatePayload) => {
    showLoadingRef.current?.('loading', 'Actualizando orden...');
    updateSelectionOrder(orderId, payload, {
      onSuccess: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.success(resp.message);
        setErrorFields(undefined);

        load(orderId);

        setReloadTick(prev => prev + 1);
      },
      onError: (resp: ServiceResponse) => {
        hideLoadingRef.current?.();
        toast.error(resp.message);
        // Revertir cambios locales recargando la orden desde el backend
        load(orderId);
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoadingRef.current?.();
        setErrorFields(errs.data);

      if (errs.data?.non_field_errors) {
        const nonFieldErrors = errs.data.non_field_errors;
        if (Array.isArray(nonFieldErrors)) {
          nonFieldErrors.forEach((error: string) => {
            toast.error(error, { autoClose: 5000 });
          });
        } else if (typeof nonFieldErrors === 'string') {
          toast.error(nonFieldErrors, { autoClose: 5000 });
        }
      }

      if (!errs.data?.non_field_errors) {
        toast.error('Error de validación. Por favor revise los campos.', { autoClose: 3000 });
      }

      // En errores de validación también recargamos la orden para
      // no dejar el formulario en un estado incoherente (por ejemplo,
      // mostrando FINALIZED cuando la orden sigue en progreso).
      load(orderId);
      }
    });
  };

  const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Órdenes de Recolección', url: '/selection-orders', isActive: false },
    { name: `Editar Orden #${orderId}`, url: `/selection-orders/${orderId}/edit`, isActive: true },
  ];

  const formatDate = (d?: string | null) => {
    if (!d) return '';
    try { const date = new Date(d); return Number.isNaN(date.getTime()) ? (d as string) : date.toLocaleDateString(); }
    catch { return d as string; }
  };

  const createdByName =
    (order as any).created_by_name ||
    (order as any).created_by_fullname ||
    [ (order as any)?.created_by?.first_name, (order as any)?.created_by?.last_name ].filter(Boolean).join(' ') ||
    (order as any)?.created_by?.username || '';

  const updatedByName =
    (order as any).updated_by_name ||
    (order as any).updated_by_fullname ||
    [ (order as any)?.updated_by?.first_name, (order as any)?.updated_by?.last_name ].filter(Boolean).join(' ') ||
    (order as any)?.updated_by?.username || '';

  // Separar pending y loaded
  const pendingRows = order.pending_batches || [];
  const loadedRows = order.loaded_batches || [];

  if (verifying) {
    return (
      <DefaultCard>
        <LazyLoading height={300} />
      </DefaultCard>
    );
  }

  return (
    <>
      <Breadcrumbs pageSection={`Editar Orden #${orderId}`} breadcrumbs={breadcrumbs} />

      {/* Tabs superiores */}
      <Tabs defaultActiveKey="general" id="co-edit-top-tabs" className="mb-3">
        {/* === TAB 1: Datos Generales === */}
        <Tab eventKey="general" title="Datos Generales">

          {/* Fila con 2 contenedores separados */}
          <div className="row">
            {/* Contenedor izquierdo: Datos Generales */}
            <div className="col-12 col-lg-8">
              <DefaultCard>
                <h5 className="mb-3">Datos Generales</h5>
                <SelectionOrderHeaderForm
                  fetching={!!(fetchingEditSelectionOrder || fetchingUpdateSelectionOrder)}
                  order={order}
                  setOrder={setOrder}
                  societies={societies}
                  units={units}
                  statuses={statuses}
                  errorFields={errorFields}
                  onCancel={() => window.history.back()}
                  onSave={(o) => {
                    const toYMD = (d: any) => {
                      if (!d) return undefined;
                      if (typeof d === 'string') return d.slice(0, 10);
                      if (d instanceof Date && !isNaN(d.getTime())) return formatDateToInputDate(d);
                      return undefined;
                    };

                    const programDate = toYMD((o as any).program_date);
                    const startDate = toYMD((o as any).start_date);
                    const base: SelectionOrderUpdatePayload = {
                      society_id: o.society_id ?? null,
                      ...(programDate !== undefined ? { program_date: programDate } : {}),
                      ...(startDate !== undefined ? { start_date: startDate } : {}),
                      status: o.status as any,
                      is_active: o.is_active,
                      planned_quantity: o.planned_quantity ?? null,
                      planned_unit_id: o.planned_unit_id ?? null,
                    };

                    const maybeEnd = toYMD((o as any).end_date);

                    // ✅ 1) No permitir FINALIZED sin end_date
                    if (o.status === 'FINALIZED' && !maybeEnd) {
                      toast.error('Para finalizar debe indicar la Fecha Fin.');
                      return;
                    }

                    // Enviar end_date sólo si FINALIZED (con valor)
                    const payload: SelectionOrderUpdatePayload =
                      o.status === 'FINALIZED'
                        ? { ...base, end_date: maybeEnd! }
                        : base;

                    onSaveHeader(payload);
                  }}
                />
              </DefaultCard>
            </div>

            {/* Contenedor derecho: Datos del creador */}
            <div className="col-12 col-lg-4">
              <DefaultCard>
                <h5 className="mb-3">Datos del creador</h5>
                <div className="mb-2">
                  <label className="form-label">Creado por</label>
                  <input className="form-control" value={createdByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de creación</label>
                  <input className="form-control" value={formatDate((order as any).created_at)} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Editado por</label>
                  <input className="form-control" value={updatedByName} disabled />
                </div>
                <div className="mb-2">
                  <label className="form-label">Fecha de actualización</label>
                  <input className="form-control" value={formatDate((order as any).updated_at)} disabled />
                </div>
              </DefaultCard>
            </div>
          </div>

          {/* Contenedor inferior: pestañas con tablas (Lotes / Usuarios) */}
          <DefaultCard>
            <Tabs defaultActiveKey="batches" id="selection-order-subtabs" className="mb-3">
              <Tab eventKey="batches" title="Lotes asignados">
                <div className="col-12">
                  <OrderBatchesTab
                    orderId={orderId}
                    pendingRows={order.pending_batches ?? []}
                    destinationRows={order.destination_batches ?? []}
                    onChanged={() => load(orderId)}
                    readOnly={order?.status === 'FINALIZED'}
                  />
                </div>
              </Tab>
              <Tab eventKey="collectors" title="Usuarios asignados">
                <div className="col-12">
                  <OrderCollectorsTab
                    orderId={orderId}
                    rows={(order.collectors as SelectionOrderCollectorRow[]) || []}
                    onChanged={() => load(orderId)}
                  />
                </div>
              </Tab>
            </Tabs>
          </DefaultCard>
        </Tab>

        {/* === TAB 2: Incidentes (NUEVA) === */}
        <Tab eventKey="incidents" title="Incidentes">
          <OrderIncidentsTab
              key={reloadTick}           // remount si guardas cabecera
              orderId={orderId}
              onChanged={() => setReloadTick(prev => prev + 1)} // refresca otras pestañas si quieres
          />
        </Tab>

        {/* === TAB 3: Historial === */}
        <Tab eventKey="history" title="Historial">
          <OrderHistoryTab key={reloadTick} orderId={orderId} />
        </Tab>
      </Tabs>
    </>
  );
};

export default SelectionOrdersEdit;